<script setup>
import { nextTick, onBeforeUnmount, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },

  title: {
    type: String,
    default: '',
  },

  description: {
    type: String,
    default: '',
  },

  size: {
    type: String,
    default: 'md',
    validator: (value) =>
      ['sm', 'md', 'lg'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue']);

function close() {
  emit('update:modelValue', false);
}

function handleKeydown(event) {
  if (event.key === 'Escape') {
    close();
  }
}

function syncBodyLock(isOpen) {
  if (typeof document === 'undefined') {
    return;
  }

  document.body.classList.toggle(
    'modal-open',
    isOpen
  );
}

watch(
  () => props.modelValue,
  async (isOpen) => {
    syncBodyLock(isOpen);

    if (isOpen) {
      await nextTick();

      document
        .querySelector(
          '[data-modal-dialog]:not([hidden])'
        )
        ?.focus();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  syncBodyLock(false);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="base-modal"
      role="presentation"
      @mousedown.self="close"
    >
      <div
        class="base-modal__backdrop"
        aria-hidden="true"
      ></div>

      <section
        class="base-modal__dialog"
        :class="`base-modal__dialog--${size}`"
        role="dialog"
        aria-modal="true"
        :aria-label="title || undefined"
        tabindex="-1"
        data-modal-dialog
      >
        <header class="base-modal__header">
          <div class="base-modal__heading">
            <h2 v-if="title">
              {{ title }}
            </h2>

            <p v-if="description">
              {{ description }}
            </p>
          </div>

          <button
            type="button"
            class="base-modal__close"
            aria-label="Cerrar"
            @click="close"
          >
            <ion-icon
              name="close-outline"
              aria-hidden="true"
            ></ion-icon>
          </button>
        </header>

        <div class="base-modal__body">
          <slot />
        </div>

        <footer
          v-if="$slots.footer"
          class="base-modal__footer"
        >
          <slot name="footer" />
        </footer>
      </section>
    </div>
  </Teleport>
</template>
